import React, { useState, useEffect, useRef } from "react";
import styles from "./commercials-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Button from "../../ui/button";
import Table from "../../ui/table";
import ContactItem from "../../screens/contact-item";
import EditContactModal from "../edit-contact-modal";
import FormSection from "../../screens/form-section";
import { getCommercials, createCommercial, getCommercialById, editCommercial, deleteCommercial } from "../../../services/apiService";

const CommercialsModal = ({ isOpen, onClose, initialData }) => {
  const modalRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    dni: "",
    address: "",
    observations: "",
    contacts: [],
  });
  const [commercials, setCommercials] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCommercialId, setEditingCommercialId] = useState(null);
  
  // Estados para contactos
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState(initialData?.phoneNumber || "");
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    // Actualizar teléfono de contacto si se proporciona initialData
    if (initialData?.phoneNumber) {
      setContactPhone(initialData.phoneNumber);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchCommercials = async () => {
      try {
        const commercialsData = await getCommercials();
        // Transformar los datos para incluir el nombre y teléfono del primer contacto
        const transformedData = commercialsData.map(commercial => ({
          ...commercial,
          nombreContacto: commercial.contactos && commercial.contactos.length > 0 
            ? commercial.contactos[0].nombre 
            : '',
          telefono: commercial.contactos && commercial.contactos.length > 0 
            ? commercial.contactos[0].telefono 
            : ''
        }));
        setCommercials(transformedData);
      } catch (error) {
        console.error("Error fetching commercials:", error);
      }
    };

    if (isOpen) {
      fetchCommercials();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // No cerrar si el EditContactModal está abierto
      if (isEditContactModalOpen) {
        return;
      }
      
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isEditContactModalOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddCommercial = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        alert("El nombre es obligatorio");
        return;
      }
      if (formData.email && !validateEmail(formData.email)) {
        alert("El correo no es válido");
        return;
      }

      const commercialData = {
        nombre: formData.name,
        correo: formData.email,
        dni: formData.dni,
        domicilio: formData.address,
        observaciones: formData.observations,
        contactos: formData.contacts,
      };

      await createCommercial(commercialData);
      
      // Refrescar la lista de comerciales
      const commercialsData = await getCommercials();
      const transformedData = commercialsData.map(commercial => ({
        ...commercial,
        nombreContacto: commercial.contactos && commercial.contactos.length > 0 
          ? commercial.contactos[0].nombre 
          : '',
        telefono: commercial.contactos && commercial.contactos.length > 0 
          ? commercial.contactos[0].telefono 
          : ''
      }));
      setCommercials(transformedData);
      
      // Limpiar el formulario
      setFormData({
        name: "",
        email: "",
        dni: "",
        address: "",
        observations: "",
        contacts: [],
      });
      setContactName("");
      setContactPhone("");
      
    } catch (error) {
      console.error("Error al crear comercial:", error);
    }
  };

  const handleEditCommercial = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        alert("El nombre es obligatorio");
        return;
      }
      if (formData.email && !validateEmail(formData.email)) {
        alert("El correo no es válido");
        return;
      }

      const commercialData = {
        nombre: formData.name,
        correo: formData.email,
        dni: formData.dni,
        domicilio: formData.address,
        observaciones: formData.observations,
        contactos: formData.contacts,
      };

      await editCommercial(editingCommercialId, commercialData);

      // Refrescar la lista de comerciales
      const commercialsData = await getCommercials();
      const transformedData = commercialsData.map(commercial => ({
        ...commercial,
        nombreContacto: commercial.contactos && commercial.contactos.length > 0 
          ? commercial.contactos[0].nombre 
          : '',
        telefono: commercial.contactos && commercial.contactos.length > 0 
          ? commercial.contactos[0].telefono 
          : ''
      }));
      setCommercials(transformedData);

      // Limpiar el formulario y salir del modo edición
      setFormData({
        name: "",
        email: "",
        dni: "",
        address: "",
        observations: "",
        contacts: [],
      });
      setContactName("");
      setContactPhone("");
      setIsEditMode(false);
      setEditingCommercialId(null);
    } catch (error) {
      console.error("Error al editar comercial:", error);
    }
  };

  const handleCommercialRowClick = async (commercial) => {
    try {
      const commercialDetails = await getCommercialById(commercial.id);

      // Llenar el formulario con los datos del comercial
      setFormData({
        name: commercialDetails.nombre || "",
        email: commercialDetails.correo || "",
        dni: commercialDetails.dni || "",
        address: commercialDetails.domicilio || "",
        observations: commercialDetails.observaciones || "",
        contacts: commercialDetails.contactos || [],
      });

      setIsEditMode(true);
      setEditingCommercialId(commercial.id);
    } catch (error) {
      console.error("Error al cargar los detalles del comercial:", error);
    }
  };
  
  // Funciones de manejo de contactos
  const handleAddContact = () => {
    if (!contactName.trim() || !contactPhone.trim()) {
      alert("Por favor completa el nombre y teléfono del contacto");
      return;
    }

    const newContact = {
      nombre: contactName.trim(),
      telefono: contactPhone.trim(),
    };

    setFormData({
      ...formData,
      contacts: [...formData.contacts, newContact],
    });

    setContactName("");
    setContactPhone("");
  };

  const handleRemoveContact = (contactId) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((_, index) => index !== contactId),
    });
  };

  const handleEditContact = (contact, index) => {
    setEditingContact({ ...contact, index });
    setIsEditContactModalOpen(true);
  };

  const handleContactClick = (contact, index) => {
    handleEditContact(contact, index);
  };

  const handleSaveContact = (editedContact) => {
    const updatedContacts = [...formData.contacts];
    updatedContacts[editingContact.index] = {
      nombre: editedContact.name,
      telefono: editedContact.phone,
    };

    setFormData({
      ...formData,
      contacts: updatedContacts,
    });

    setIsEditContactModalOpen(false);
    setEditingContact(null);
  };

  const handleDeleteCommercial = async (commercialId) => {
    try {
      await deleteCommercial(commercialId);

      // Refrescar la lista de comerciales
      const commercialsData = await getCommercials();
      const transformedData = commercialsData.map(commercial => ({
        ...commercial,
        nombreContacto: commercial.contactos && commercial.contactos.length > 0 
          ? commercial.contactos[0].nombre 
          : '',
        telefono: commercial.contactos && commercial.contactos.length > 0 
          ? commercial.contactos[0].telefono 
          : ''
      }));
      setCommercials(transformedData);
    } catch (error) {
      console.error("Error al eliminar comercial:", error);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: "",
      email: "",
      dni: "",
      address: "",
      observations: "",
      contacts: [],
    });
    setContactName("");
    setContactPhone("");
    setIsEditMode(false);
    setEditingCommercialId(null);
  };

  const headers = [
    { label: "Nombre", key: "nombre" },
    { label: "Contacto", key: "nombreContacto" },
    { label: "Teléfono", key: "telefono" },
    { label: "Correo", key: "correo" },
    { label: "Observaciones", key: "observaciones" },
  ];

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Comerciales</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons
              name="close"
              width={24}
              height={24}
              color="black"
            />
          </button>
        </div>

        <FormSection title="Datos del Comercial" style={{ marginBottom: "12px" }}>
          <div className={styles.sectionRow}>
            <Input
              label="Nombre"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Correo"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="DNI"
              name="dni"
              value={formData.dni}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
          </div>
          <div className={styles.sectionRow}>
            <Input
              label="Domicilio"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Observaciones"
              name="observations"
              value={formData.observations}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
          </div>
        </FormSection>
          
        {/* Sección de Contactos */}
        <FormSection title="Contactos" style={{ marginBottom: "12px" }}>
            <div className={styles.sectionRow}>
              <Input
                label="Nombre"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Nombre del contacto"
                style={{ flex: 1 }}
              />
              <Input
                label="Teléfono"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="Teléfono"
                style={{ flex: 1 }}
              />
            </div>
            
            {/* Lista de contactos */}
            {formData.contacts && formData.contacts.length > 0 && (
              <div className={styles.contactsList}>
                {formData.contacts.map((contact, index) => (
                  <ContactItem
                    key={index}
                    contact={{
                      id: index,
                      name: contact.nombre,
                      phone: contact.telefono,
                      address: "",
                    }}
                    onRemove={handleRemoveContact}
                    onClick={() => handleContactClick(contact, index)}
                    onEdit={() => handleEditContact(contact, index)}
                  />
                ))}
              </div>
            )}
            
            <Button 
              type="button" 
              variant="secondary"
              onClick={handleAddContact}
              style={{ marginTop: "24px" }}
            >
              AÑADIR CONTACTO
            </Button>
          </FormSection>

        <div style={{ marginTop: "12px", marginBottom: "24px", display: "flex", gap: "16px" }}>
          {isEditMode ? (
            <>
              <Button type="button" variant="primary" onClick={handleEditCommercial}>
                EDITAR COMERCIAL
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancelEdit}>
                CANCELAR
              </Button>
            </>
          ) : (
            <Button type="button" variant="primary" onClick={handleAddCommercial}>
              AÑADIR COMERCIAL
            </Button>
          )}
        </div>

        <div className={styles.tableSection}>
          <Table 
            headers={headers} 
            data={commercials} 
            onPress={handleCommercialRowClick}
          />
        </div>
      </div>
      
      <EditContactModal
        isOpen={isEditContactModalOpen}
        onClose={() => {
          setIsEditContactModalOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact ? {
          ...editingContact,
          name: editingContact.nombre,
          phone: editingContact.telefono,
        } : null}
        onSave={handleSaveContact}
        rolOptions={[]}
        simpleMode={true}
      />
    </div>
  );
};

export default CommercialsModal;
